import axios from 'utils/axios'
import { API_ERROR_FALLBACK, ApiError, envelopeMessage } from 'api/errors'
import { User } from 'types'
import type { SignupAttribution } from 'utils/attribution'

// What the sign-up form says when no account was made and the API gave no
// words the customer can act on: a crash, an outage, no answer, a blank message.
export const SIGN_UP_FALLBACK =
  "We couldn't create your account just now. Please try again, or email support@getgoldie.co.nz & we'll sort it."

export const SIGN_UP_TOO_MANY =
  'Too many attempts just now. Please wait a few minutes & try again.'

// Two Cognito refusals in Goldie's words. The pool's password policy wants 8 or
// more characters with a capital, a lowercase letter, a number and a symbol, and
// the strength meter (zxcvbn) can call a password without them "Excellent".
const SIGN_UP_REFUSALS: Record<string, string> = {
  UsernameExistsException:
    'An account with this email already exists. Please log in.',
  InvalidPasswordException:
    'Your password needs 8 or more characters, including a capital letter, a lowercase letter, a number & a special character.'
}

// The words the API itself gave: a HTTP-200 { status: false } envelope (an
// ApiError from the response interceptor) or a 4xx carrying the same envelope.
// Never a 5xx's "Internal server error", which nobody can act on.
function serverMessage(error: unknown): string | undefined {
  if (error instanceof ApiError) {
    return error.message === API_ERROR_FALLBACK
      ? undefined
      : envelopeMessage(error.message)
  }
  const response = (
    error as { response?: { status?: number; data?: unknown } } | undefined
  )?.response
  if (!response || (response.status ?? 500) >= 500) return undefined
  const data = response.data as { status?: unknown; message?: unknown } | null
  return data !== null && typeof data === 'object' && data.status === false
    ? envelopeMessage(data.message)
    : undefined
}

// Every refused sign-up used to read "Sign up failed. Please try again.",
// whatever the cause: a refused referrer code, a password Cognito would not
// take, a closed account and an outage all looked the same (a prospect, 16 Oct
// 2026). Now the customer sees the API's own explanation whenever it gave one,
// Goldie's wording for the two Cognito refusals above, and otherwise
// SIGN_UP_FALLBACK, which says what to do next.
export function signUpErrorMessage(error: unknown): string {
  const err = ApiError.fromAxiosError(error)
  const refusal = SIGN_UP_REFUSALS[err.code]
  if (refusal) return refusal
  if (err.status === 429) return SIGN_UP_TOO_MANY
  return serverMessage(error) ?? SIGN_UP_FALLBACK
}

function signUpError(cause: unknown): Error & { friendlyMessage: string } {
  const message = signUpErrorMessage(cause)
  const err = new Error(message) as Error & { friendlyMessage: string }
  err.friendlyMessage = message
  return err
}

export async function getCurrentUser(): Promise<User> {
  const response = await axios.get('user/me')

  return response.data as User
}

export async function signUp(params: {
  email: string
  password: string
  signup_token?: string
  recaptcha_token?: string
  marketing_opt_in?: boolean
  referrer_code?: string
  user_name?: string
  // Which link and channel brought them (utils/attribution.ts); the backend
  // records it while its referral_attribution switch is on and ignores it
  // otherwise. The code itself travels as referrer_code.
  attribution?: SignupAttribution
}): Promise<{ userSub: string; userConfirmed: boolean }> {
  // Serverless /auth/signup: flat body, creates the Cognito user AND the backing
  // app `users` row, and auto-confirms (no email code). A fail (e.g. duplicate
  // email) is a HTTP-200 { status:false } envelope, which the response
  // interceptor now REJECTS as an ApiError carrying the Cognito error `code`.
  // signUpErrorMessage picks the words; on success the envelope unwraps to
  // { userSub, userConfirmed }.
  let response
  try {
    response = await axios.post('/auth/signup', {
      user_email: params.email,
      user_name: params.user_name ?? '',
      password: params.password,
      // recaptcha_token is intentionally NOT forwarded: serverless /auth/signup
      // ignores it. axios omits undefined values, so unset optional fields below
      // simply aren't serialized.
      referrer_code: params.referrer_code,
      marketing_opt_in: params.marketing_opt_in,
      signup_token: params.signup_token,
      attribution: params.attribution
    })
  } catch (err) {
    throw signUpError(err)
  }

  const data = response.data as {
    userSub?: string
    userConfirmed?: boolean
    code?: string
  }

  if (!data?.userSub) {
    throw signUpError(new ApiError('', data?.code ?? 'NO_USER_SUB', 200))
  }

  return { userSub: data.userSub, userConfirmed: !!data.userConfirmed }
}

export async function confirmEmail({
  email,
  confirmation_token,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  recaptcha_token: _recaptcha_token
}: {
  email: string
  confirmation_token: string
  recaptcha_token?: string
}): Promise<void> {
  await axios.post('/auth/confirm-signup', {
    email,
    confirmationCode: confirmation_token
  })
}

export async function resendConfirmationEmail({
  email,
  recaptcha_token
}: {
  email: string
  recaptcha_token?: string
}): Promise<{ status: string }> {
  const response = await axios.post('/auth/resend-code', {
    email,
    recaptcha_token
  })

  return response.data
}

export async function requestResetPassword({
  email
}: {
  email: string
  recaptcha_token?: string
}): Promise<{ status: string }> {
  // Serverless: POST /auth/forgot-password { email } emails a 6-digit reset
  // code (the non-existent /user/passwords endpoint never did anything).
  await axios.post('/auth/forgot-password', { email })

  return { status: 'ok' }
}

export async function resetPassword({
  email,
  confirmationCode,
  newPassword
}: {
  email: string
  confirmationCode: string
  newPassword: string
  recaptcha_token?: string
}): Promise<{ status: string }> {
  // Serverless: POST /auth/reset-password { email, confirmationCode,
  // newPassword } completes the Cognito ForgotPassword flow. Requires the
  // emailed 6-digit code — there is no reset link token.
  await axios.post('/auth/reset-password', {
    email,
    confirmationCode,
    newPassword
  })

  return { status: 'ok' }
}

export async function createUserInvite(params: {
  name: string
  email: string
  recaptcha_token?: string
  utm_id?: string
}): Promise<unknown> {
  const { recaptcha_token, utm_id, ...user_invite } = params

  const response = await axios.post('user_invites', {
    user_invite,
    recaptcha_token,
    utm_id
  })

  return response.data
}
